/**
 * BlockEventManager - Calculates real-time metrics from block history
 *
 * Maintains a rolling history of blocks and calculates:
 * - Hashrate (24-hour average)
 * - Average block time (144 blocks)
 * - Difficulty adjustment progress
 * - Mining pool distribution
 *
 * Data sources (WebSocket-first for speed):
 * 1. Mempool WebSocket - Real-time blocks (PRIMARY - fast)
 * 2. Diamondback API - Historical seed (OPTIONAL - background enhancement)
 * 3. Client calculations - Fallback from block history
 *
 * Works with as few as 2 blocks from WebSocket, enhanced by Diamondback when available.
 * Triggers selective API refreshes on new blocks to minimize API calls.
 */

const DIAMONDBACK_BASE_URL = 'https://2140data.com/api';

export class BlockEventManager {
  constructor() {
    this.blockHistory = [];
    this.lastBlockHeight = 0;
    this.maxHistory = 4032; // 4 weeks (~Diamondback capacity)
    this.isSeeded = false;
    this.hasGaps = false; // True if Diamondback data has missing blocks
  }

  /**
   * Seed block history from Diamondback API (OPTIONAL - background enhancement)
   * Fetches last 144 blocks (~24 hours). Non-blocking with timeout.
   * If this fails, WebSocket blocks will be used instead.
   */
  async seedFromDiamondback() {
    // Skip if already seeded or if we have WebSocket blocks
    if (this.isSeeded) {
      console.log('[BlockEventManager] Already seeded, skipping Diamondback fetch');
      return { success: true, blockCount: this.blockHistory.length, skipped: true };
    }

    console.log('[BlockEventManager] Seeding from Diamondback (background)...');

    try {
      // Add timeout to prevent blocking if Diamondback is slow
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000); // 15 second timeout

      // Fetch 144 blocks (~24 hours worth)
      const response = await fetch(`${DIAMONDBACK_BASE_URL}/blocks/144`, {
        signal: controller.signal
      });
      clearTimeout(timeout);

      if (!response.ok) {
        throw new Error(`Diamondback API error: ${response.status}`);
      }

      const blocks = await response.json();

      // Convert Diamondback format to our internal format
      const convertedBlocks = blocks.map(block => ({
        height: block.height,
        timestamp: block.timestamp,
        difficulty: block.difficulty,
        pool: block.extras?.pool?.name || 'Unknown'
      }));

      // Sort by height ascending (oldest first)
      convertedBlocks.sort((a, b) => a.height - b.height);

      // Check for missing blocks (gaps in heights)
      const minHeight = convertedBlocks[0]?.height;
      const maxHeight = convertedBlocks[convertedBlocks.length - 1]?.height;
      const expectedCount = maxHeight - minHeight + 1;
      const actualCount = convertedBlocks.length;
      const missingBlocks = expectedCount - actualCount;

      if (missingBlocks > 0) {
        console.warn(`[BlockEventManager] Diamondback has ${missingBlocks} missing blocks (${actualCount}/${expectedCount}). Data unreliable.`);
        // Mark as seeded but flag that data is incomplete
        this.hasGaps = true;
      } else {
        this.hasGaps = false;
      }

      // Merge with any existing WebSocket blocks (keep newer blocks from WebSocket)
      if (this.blockHistory.length > 0) {
        const existingHeights = new Set(this.blockHistory.map(b => b.height));
        const newBlocks = convertedBlocks.filter(b => !existingHeights.has(b.height));
        this.blockHistory = [...newBlocks, ...this.blockHistory].sort((a, b) => a.height - b.height);
      } else {
        this.blockHistory = convertedBlocks;
      }

      this.lastBlockHeight = this.blockHistory[this.blockHistory.length - 1]?.height || 0;
      this.isSeeded = true;

      console.log(`[BlockEventManager] Seeded with ${blocks.length} blocks (${convertedBlocks[0]?.height} → ${this.lastBlockHeight})`);

      return {
        success: true,
        blockCount: blocks.length,
        heightRange: {
          start: convertedBlocks[0]?.height,
          end: this.lastBlockHeight
        }
      };
    } catch (error) {
      const isTimeout = error.name === 'AbortError';
      console.warn(`[BlockEventManager] Diamondback seed ${isTimeout ? 'timed out' : 'failed'}: ${error.message}`);
      console.log('[BlockEventManager] Will use WebSocket blocks instead (this is fine)');
      // Don't mark as failed - WebSocket will provide blocks
      return { success: false, error: error.message, timeout: isTimeout };
    }
  }

  /**
   * Process new block and calculate metrics
   * @param {Object} block - Block data from Mempool WebSocket
   * @param {Object} callbacks - Event callbacks
   */
  onNewBlock(block, callbacks = {}) {
    // Validate genuine new block
    if (!block || block.height <= this.lastBlockHeight) {
      return;
    }

    // Store in history
    this.blockHistory.push({
      height: block.height,
      timestamp: block.timestamp,
      difficulty: block.difficulty,
      pool: block.extras?.pool?.name || 'Unknown'
    });

    // Trim to max history
    if (this.blockHistory.length > this.maxHistory) {
      this.blockHistory.shift();
    }

    this.lastBlockHeight = block.height;

    // Calculate real-time metrics
    const metrics = {
      hashrate: this.calculateHashrate(),
      avgBlockTime: this.calculateAvgBlockTime(),
      difficultyProgress: this.calculateDifficultyProgress(block),
      poolDistribution: this.calculatePoolDistribution(),
      blocks24h: this.calculateBlocks24h()
    };

    // Notify callbacks
    if (callbacks.onMetricsCalculated) {
      callbacks.onMetricsCalculated(metrics);
    }

    // Trigger selective endpoint refresh
    if (callbacks.onRefreshNeeded) {
      // Only refresh network stats that change on each block
      callbacks.onRefreshNeeded(['s01', 's02', 's03', 's06']);
    }
  }

  /**
   * Calculate average block time from blocks in last 24 hours
   * @param {Array} blocks - Optional pre-fetched blocks array
   * @returns {number|null} Average time in seconds
   */
  calculateAvgBlockTime(blocks = null) {
    const blockList = blocks || this.getBlocks24h();
    if (blockList.length < 2) return null;

    const oldestTimestamp = blockList[0].timestamp;
    const newestTimestamp = blockList[blockList.length - 1].timestamp;
    const timeSpan = newestTimestamp - oldestTimestamp;

    // Need at least 2 hours of data for reliable calculation
    if (timeSpan < 7200) return null;

    return timeSpan / (blockList.length - 1);
  }

  /**
   * Calculate network hashrate from blocks in last 24 hours
   * @returns {number|null} Hashrate in EH/s
   */
  calculateHashrate() {
    const blocks = this.getBlocks24h();
    if (blocks.length < 2) return null;

    const avgBlockTime = this.calculateAvgBlockTime(blocks);
    if (!avgBlockTime) return null;

    const difficulty = blocks[blocks.length - 1].difficulty;

    // Hashrate = (difficulty * 2^32) / avgBlockTime
    const hashrate = (difficulty * Math.pow(2, 32)) / avgBlockTime;

    // Convert to EH/s (exahashes per second)
    return hashrate / 1e18;
  }

  /**
   * Calculate difficulty adjustment progress
   * @param {Object} currentBlock - Latest block
   * @returns {Object} Progress stats
   */
  calculateDifficultyProgress(currentBlock) {
    const RETARGET_INTERVAL = 2016;
    const periodStart = Math.floor(currentBlock.height / RETARGET_INTERVAL) * RETARGET_INTERVAL;
    const blocksInPeriod = currentBlock.height - periodStart;
    const progress = (blocksInPeriod / RETARGET_INTERVAL) * 100;

    const avgBlockTime = this.calculateAvgBlockTime();
    const blocksRemaining = RETARGET_INTERVAL - blocksInPeriod;
    const estimatedTime = avgBlockTime ? avgBlockTime * blocksRemaining : null;

    return {
      progress,
      blocksInPeriod,
      blocksRemaining,
      estimatedTime, // Time in seconds until next adjustment
      nextHeight: periodStart + RETARGET_INTERVAL
    };
  }

  /**
   * Calculate mining pool distribution from recent blocks
   * @returns {Array} Pool stats sorted by block count
   */
  calculatePoolDistribution() {
    const blocks = this.blockHistory.slice(-100); // Last ~100 blocks
    if (blocks.length === 0) return [];

    const poolCounts = {};

    blocks.forEach(block => {
      const pool = block.pool || 'Unknown';
      poolCounts[pool] = (poolCounts[pool] || 0) + 1;
    });

    return Object.entries(poolCounts)
      .map(([pool, count]) => ({
        pool,
        blocks: count,
        percentage: (count / blocks.length) * 100
      }))
      .sort((a, b) => b.blocks - a.blocks);
  }

  /**
   * Get blocks mined in last 24 hours (by timestamp)
   * @returns {Array} Blocks with timestamp within last 24 hours
   */
  getBlocks24h() {
    if (this.blockHistory.length < 2) return [];

    const now = Math.floor(Date.now() / 1000);
    const twentyFourHoursAgo = now - 86400;

    return this.blockHistory.filter(
      block => block.timestamp >= twentyFourHoursAgo
    );
  }

  /**
   * Calculate actual blocks mined in last 24 hours (by timestamp).
   * Returns null unless block history actually spans ~24 hours, otherwise the
   * count is meaningless (e.g. 8 blocks accumulated since page load is not "8 blocks/24h").
   * @returns {number|null} Count of blocks mined in last 24 hours, or null if history is too short
   */
  calculateBlocks24h() {
    const blocks = this.getBlocks24h();
    if (blocks.length === 0) return null;

    // Require oldest block to be at least 22h old before trusting the count.
    // Anything shorter is an undercount of the real 24h window.
    const now = Math.floor(Date.now() / 1000);
    const oldestAge = now - blocks[0].timestamp;
    if (oldestAge < 79200) return null; // 22h in seconds

    return blocks.length;
  }

  /**
   * Get current block history
   * @returns {Array} Block history
   */
  getBlockHistory() {
    return [...this.blockHistory];
  }

  /**
   * Get latest block
   * @returns {Object|null} Most recent block
   */
  getLatestBlock() {
    return this.blockHistory.length > 0
      ? this.blockHistory[this.blockHistory.length - 1]
      : null;
  }

  /**
   * Check if block history is seeded with sufficient data
   * @returns {boolean} True if seeded with at least 2 hours of block history
   */
  isSeededFromDiamondback() {
    if (!this.isSeeded || this.blockHistory.length < 2) return false;

    const timeSpan = this.blockHistory[this.blockHistory.length - 1].timestamp - this.blockHistory[0].timestamp;
    return timeSpan >= 7200; // At least 2 hours
  }

  /**
   * Get block history statistics
   * @returns {Object} Stats about block history
   */
  getStats() {
    return {
      blockCount: this.blockHistory.length,
      isSeeded: this.isSeeded,
      hasGaps: this.hasGaps,
      lastHeight: this.lastBlockHeight,
      oldestBlock: this.blockHistory[0]?.height || null,
      newestBlock: this.lastBlockHeight,
      maxCapacity: this.maxHistory
    };
  }

  /**
   * Reset block history (useful for testing)
   */
  reset() {
    this.blockHistory = [];
    this.lastBlockHeight = 0;
    this.isSeeded = false;
    this.hasGaps = false;
  }
}
